import React, { useState, useMemo } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectCatering({ project, updateProject }) {
  const [newItem, setNewItem] = useState({ course: 'Main Course', name: '', description: '', tags: '' });
  
  const menuItems = project.cateringMenu || [];
  const guests = project.guests || [];

  const courses = ['Hors d\'oeuvres', 'Starter / Salad', 'Main Course', 'Sides', 'Dessert', 'Late Night Snack', 'Drinks / Bar'];

  // Smart feature: Automatically pull dietary restrictions from the Guest List manager!
  const dietaryRestrictions = useMemo(() => {
    return guests
      .filter(g => g.rsvp !== 'Declined' && g.dietary && g.dietary.trim() !== '')
      .map(g => ({ name: g.name, notes: g.dietary }));
  }, [guests]);

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;

    updateProject(project.id, {
      cateringMenu: [...menuItems, { ...newItem, id: Date.now().toString() }]
    });
    
    setNewItem({ course: newItem.course, name: '', description: '', tags: '' });
  };

  const handleRemoveItem = (id) => {
    if (window.confirm("Remove this item from the menu?")) {
      updateProject(project.id, {
        cateringMenu: menuItems.filter(item => item.id !== id)
      });
    }
  };

  // Group items by course
  const groupedMenu = courses.reduce((acc, course) => {
    const items = menuItems.filter(item => item.course === course);
    if (items.length > 0) acc[course] = items;
    return acc;
  }, {});

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in flex flex-col h-full">
      {/* Header & Stats */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-6">
            <div>
                <h2 className="text-2xl font-bold font-serif text-gray-900">Catering & Menu</h2>
                <p className="text-sm text-gray-500 mt-1">Plan the food and beverage offerings and track dietary restrictions.</p>
            </div>
            <div className="bg-orange-50 px-4 py-3 rounded-xl border border-orange-100 flex-1 sm:flex-none text-center sm:text-left">
                <p className="text-[10px] font-bold text-orange-600 uppercase tracking-widest mb-1">Menu Items</p>
                <p className="text-2xl font-black text-orange-700">{menuItems.length}</p>
            </div>
        </div>

        <div className="bg-rose-50 rounded-xl border border-rose-100 p-4">
            <h3 className="text-sm font-bold text-rose-900 mb-2 flex items-center gap-2">
                <span>⚠️</span> Guest Dietary Restrictions Summary
            </h3>
            {dietaryRestrictions.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-3">
                    {dietaryRestrictions.map((guest, idx) => (
                        <div key={idx} className="bg-white border border-rose-200 px-3 py-1.5 rounded-lg shadow-sm flex flex-col">
                            <span className="text-xs font-bold text-gray-800">{guest.name}</span>
                            <span className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">{guest.notes}</span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-rose-700 italic">No dietary restrictions noted by attending guests yet.</p>
            )}
        </div>
      </div>

      {/* Add Menu Item Form */}
      <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
        <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Add to Menu</h3>
        <form onSubmit={handleAddItem} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-1">
              <select
                value={newItem.course}
                onChange={(e) => setNewItem({ ...newItem, course: e.target.value })}
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-orange-500 outline-none"
              >
                {courses.map(course => <option key={course} value={course}>{course}</option>)}
              </select>
          </div>
          <div className="lg:col-span-2">
              <input
                type="text"
                required
                placeholder="Item Name (e.g. Filet Mignon)"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              />
          </div>
          <div className="lg:col-span-2">
              <input
                type="text"
                placeholder="Description (e.g. Served with garlic mash...)"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              />
          </div>
          <div className="lg:col-span-1 flex">
              <button 
                type="submit"
                disabled={!newItem.name.trim()}
                className="w-full bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-lg font-bold transition shadow-md disabled:bg-gray-300 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <PlannerIcons.Plus /> Add
              </button>
          </div>
        </form>
      </div>

      {/* Menu Grid */}
      <div className="flex-1 space-y-6">
        {Object.keys(groupedMenu).length > 0 ? (
          Object.entries(groupedMenu).map(([course, items]) => (
            <div key={course} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                        <PlannerIcons.Utensils /> {course}
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded-md">
                        {items.length} Items
                    </span>
                </div>
                <div className="divide-y divide-gray-100">
                    {items.map(item => (
                        <div key={item.id} className="p-4 flex justify-between items-start gap-4 hover:bg-gray-50 transition group">
                            <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-gray-800 text-sm">{item.name}</h4>
                                {item.description && (
                                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{item.description}</p>
                                )}
                            </div>
                            <button
                                onClick={() => handleRemoveItem(item.id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100"
                                title="Remove Item"
                            >
                                <PlannerIcons.Trash />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
          ))
        ) : (
            <div className="py-16 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                <div className="text-4xl mb-3">🍽️</div>
                <p className="font-medium text-gray-500">Your menu is currently empty.</p>
                <p className="text-sm mt-1">Add appetizers, mains, and desserts to track your event catering.</p>
            </div>
        )}
      </div>
    </div>
  );
}